import { cn } from "../lib/cn";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "subtle" | "ai";
export type ButtonSize = "xs" | "sm" | "md" | "lg";

export const BUTTON_VARIANT: Record<ButtonVariant, string> = {
  // Glow do primary tingido via --btn-primary-glow (fallback = --accent coral).
  // O estilo minimal seta transparent pra um primary mono sem halo (vos-minimal.css).
  primary:
    "bg-brand-500 text-[rgb(var(--vos-on-brand))] font-semibold shadow-[0_1px_2px_rgba(0,0,0,0.10),0_6px_16px_-8px_var(--btn-primary-glow,var(--accent))] hover:bg-brand-400 hover:shadow-[0_1px_2px_rgba(0,0,0,0.12),0_8px_20px_-8px_var(--btn-primary-glow,var(--accent))] active:bg-brand-600 active:shadow-[0_1px_2px_rgba(0,0,0,0.10)]",
  // border-solid explícito: o reset do shell (`:where(.vos-os) button{border:0}`)
  // seta border-style none e o utilitário `border` só devolve a largura — sem
  // isso, secondary/subtle renderizam SEM borda dentro do app.
  // ELEVAÇÃO (founder 01/09, referência Shopify): o secundário era plano, só
  // borda no branco, e ao lado do primário sólido parecia um campo, não um
  // botão. Ganha o mesmo relevo de 1px do primário: sombra curta pra baixo, que
  // some no hover (o botão "afunda" ao ser tocado).
  secondary:
    "border border-solid border-[var(--line-2)] bg-[var(--panel)] text-[var(--ink)] shadow-[shadow:var(--vos-shadow-control)] hover:border-[var(--ink-3)] hover:bg-[var(--panel-2)] hover:shadow-none active:bg-[var(--panel-3)] active:shadow-none",
  ghost: "text-[var(--ink-2)] hover:bg-[var(--panel-3)] hover:text-[var(--ink)] active:bg-[var(--panel-4,var(--line-2))]",
  // DESTRUTIVO É SÓLIDO (§105, founder 24/09: "botão inteiro vermelho, ícone
  // e texto brancos em semibold"). Era um vermelho a 20% que lia como aviso,
  // não como ação: excluir não pode parecer um chip.
  danger:
    "border border-solid border-[var(--bad)] bg-[var(--bad)] font-semibold text-white hover:bg-[color-mix(in_oklab,var(--bad)_88%,black)] active:bg-[color-mix(in_oklab,var(--bad)_80%,black)]",
  subtle:
    "border border-solid border-[var(--line)] bg-[var(--panel-2)] text-[var(--ink-2)] hover:border-[var(--ink-3)] hover:bg-[var(--panel-3)] hover:text-[var(--ink)] active:bg-[var(--panel-4,var(--line-2))]",
  // Ação do Zé (§38): violeta soft — nunca compete com o primary da tela.
  ai: "bg-[var(--vos-ai-soft)] font-medium text-[var(--vos-ai)] ring-1 ring-inset ring-[color-mix(in_oklab,var(--vos-ai)_25%,transparent)] hover:ring-[color-mix(in_oklab,var(--vos-ai)_45%,transparent)]",
};

export const BUTTON_SIZE: Record<ButtonSize, string> = {
  // Alturas-alvo do DS 2.0 (§21): sm 30 · md 36 · lg 42 (Fase 4).
  // xs: linhas densas de tabela (equivalente do `.btn.xs` legado, 24px).
  xs: "h-6 px-2 text-[length:var(--fs-caption)] gap-1",
  sm: "h-[var(--vos-control-h,30px)] px-3 text-[length:var(--fs-label)] gap-1.5",
  md: "h-9 px-4 text-[length:var(--fs-sm)] gap-2",
  lg: "h-[42px] px-5 text-[length:var(--fs-sm)] gap-2",
};

// Raio via --btn-r: pill (999px) no tema Padrão "Amber Glow"; o minimal seta
// --btn-r: 6px (vos-minimal.css) pro retangular Vercel. Mesma forma do .btn CSS.
export const BUTTON_BASE =
  // PESO 550 (founder 01/09, referência Shopify): o rótulo estava em 400 e lia
// como lavado ao lado do primário, que já era semibold. A cor não era o
// problema (o nosso #171717 é mais escuro que o #303030 deles) — era o peso.
  // VOS Movimento (26/09): hover entra em 100 e sai em 150 (a base tem a
  // saída, o `hover:` tem a entrada); `opacity` na lista, senão o desabilitar
  // piscava de estalo.
  "group/vbtn inline-flex items-center justify-center font-[550] rounded-[var(--btn-r,999px)] transition-[background-color,border-color,color,transform,box-shadow,opacity] duration-vos-sm hover:duration-vos-xs ease-vos " +
  // Aperto (founder 26/09): .97 em 80ms, que se SENTE; a volta vem na base.
  // Era .985 (30/08), que ficou discreto demais pra dizer "apertei".
  "disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.97] active:duration-vos-aperto select-none whitespace-nowrap focus-visible:ring-2 focus-visible:ring-brand-500/35 " +
  "[&_svg]:pointer-events-none [&_svg]:shrink-0";

export function buttonClass(
  variant: ButtonVariant = "primary",
  size: ButtonSize = "md",
  className?: string,
) {
  return cn(BUTTON_BASE, BUTTON_VARIANT[variant], BUTTON_SIZE[size], className);
}
